/**
 * Le métier Chasseur (GDD §22). Module pur.
 *
 * Les cinq autres métiers ramassent : on choisit un gisement, on attend, la
 * ressource tombe. Le Chasseur, non — dans Dofus il ne récolte rien, il
 * dépèce ce qu'on abat. Ici il n'avait donc **aucune ressource assignée**, et se
 * retrouvait affiché parmi les six métiers sans le moindre moyen de progresser :
 * un joueur l'a demandé, et il n'y avait pas de réponse à lui donner.
 *
 * La matière existait pourtant déjà. Le contenu compte **59 viandes**, du niveau
 * 1 au niveau 200, lâchées par 837 monstres répartis dans 169 familles, et
 * consommées par 140 recettes. Elles tombaient depuis toujours ; simplement,
 * personne n'était crédité de les avoir prises.
 *
 * Le Chasseur monte donc **en combattant**. C'est aussi ce qui lui donne une
 * place à part : c'est le seul métier qui progresse pendant qu'on farme, sans
 * qu'on ait à choisir entre se battre et récolter.
 */
import type { Contenu, Item } from "./contenu.ts"

/** L'identifiant du métier, tel qu'Ankama le numérote. */
export const CHASSEUR = 41

/**
 * Ce qui compte comme gibier.
 *
 * On se fie au **type** de l'objet et non à son nom : « Viande » et « Viande
 * comestible » couvrent exactement les cinquante-neuf pièces voulues, là où une
 * recherche sur le nom attraperait la Ceinture Lardière et le Foulard du Sparo.
 */
export const estGibier = (it: Item): boolean => /^Viande/.test(it.type ?? "")

let cache: { c: Contenu; ids: Set<number> } | null = null

/** Les identifiants de tout le gibier du jeu, mémoïsés par contenu. */
export function gibiers(c: Contenu): Set<number> {
  if (cache?.c === c) return cache.ids
  const ids = new Set<number>()
  for (const it of Object.values(c.items)) if (estGibier(it)) ids.add(it.id)
  cache = { c, ids }
  return ids
}

/**
 * L'expérience que rapporte une pièce de gibier.
 *
 * Même barème que la récolte — `10 + niveau × 2` — pour que les six métiers se
 * comparent. Un Chasseur qui farme du niveau 200 monte à peu près aussi vite
 * qu'un Bûcheron sur ses meilleurs arbres.
 */
export const xpParGibier = (it: Item) => 10 + it.niveau * 2

/**
 * Ce que le niveau de Chasseur rapporte en plus.
 *
 * Les autres métiers convertissent leur niveau en **vitesse** ; le Chasseur ne
 * décide pas de sa cadence, elle appartient au combat. Son niveau lui donne donc
 * du **rendement** : une chance de ramener une pièce de plus sur chaque prise.
 * À 200, il double son gibier.
 */
export const bonusChasse = (niveau: number) => Math.min(1, Math.max(0, niveau) / 200)

/**
 * Les familles où l'on trouve du gibier, de la plus généreuse à la plus pauvre.
 *
 * Sert à répondre à la question qui vient juste après « comment on monte le
 * métier » : *où*. Une liste de 169 familles ne répondrait à rien ; on classe
 * par ce qui compte, la part de monstres de la famille qui lâchent du gibier.
 */
export function terrainsDeChasse(
  c: Contenu,
): { id: number; nom: string; niveauMin: number; part: number; zone: number | null }[] {
  const ids = gibiers(c)
  const out: { id: number; nom: string; niveauMin: number; part: number; zone: number | null }[] = []
  for (const f of Object.values(c.familles)) {
    let avec = 0
    let total = 0
    for (const mid of f.monstres) {
      const m = c.monstres[String(mid)]
      if (!m) continue
      total++
      if (m.butin.some((b) => ids.has(b.item))) avec++
    }
    if (total === 0 || avec === 0) continue
    out.push({ id: f.id, nom: f.nom, niveauMin: f.niveauMin, part: avec / total, zone: f.zone ?? null })
  }
  return out.sort((a, b) => b.part - a.part || a.niveauMin - b.niveauMin)
}
